import React, {useState, useEffect} from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    FormControl,
    Grid, MenuItem,
    Select,
    Tab,
    Tabs,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
} from '@mui/material';
import {CloudUpload, Search, ImageNotSupported, Sort, Delete} from '@mui/icons-material';
import {makeStyles} from '@mui/styles';
import Cookies from 'universal-cookie';
import {useAuth} from '../utils/useAuth';
import PhotostudioImageDetail from './PhotostudioImageDetail';
import ImageOrderDialog from './ImageOrderDialog';

const useStyles = makeStyles(() => ({
    root: {},
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '30px',
    },
    headerContent: {
        flex: 1,
    },
    button: {
        backgroundColor: '#000000 !important',
        color: 'white !important',
        boxShadow: '2px 2px #6f6f6f6b !important',
        fontFamily: "'GucciSans-Bold' !important",
        '&:hover': {
            backgroundColor: '#333333 !important',
        },
        '&:disabled': {
            backgroundColor: '#999696bf !important',
        },
    },
    filterSection: {
        marginBottom: '20px',
    },
    tabsContainer: {
        marginBottom: '20px',
    },
    imageGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '15px',
        marginBottom: '30px',
        '@media (max-width: 1200px)': {
            gridTemplateColumns: 'repeat(3, 1fr)',
        },
        '@media (max-width: 800px)': {
            gridTemplateColumns: 'repeat(2, 1fr)',
        },
    },
    imageCard: {
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        transition: 'transform 0.2s',
        '&:hover': {
            transform: 'scale(1.02)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        },
    },
    imageMedia: {
        height: '200px',
        backgroundSize: 'cover',
        position: 'relative',
    },
    menuButton: {
        backgroundColor: 'rgba(255, 255, 255, 0.9) !important',
        borderRadius: '0 !important',
        '&:hover': {
            backgroundColor: '#fff !important',
        },
    },
    cardContent: {
        padding: '12px !important',
    },
    statusBadge: {
        display: 'inline-block',
        padding: '4px 8px',
        borderRadius: '1rem',
        fontSize: '12px',
        fontWeight: 'bold',
        marginBottom: '4px',
    },
    configuredBadge: {
        backgroundColor: '#e8f5e9',
        color: '#2e7d32',
    },
    notConfiguredBadge: {
        backgroundColor: '#e3f2fd',
        color: '#1565c0',
    },
    paginationContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '10px',
        marginTop: '30px',
    },
    paginationButton: {
        minWidth: '36px',
        height: '36px',
        border: '1px solid #e0e0e0',
        borderRadius: '4px',
        backgroundColor: '#fff',
        cursor: 'pointer',
        fontFamily: "'GucciSans-Book' !important",
        '&:hover': {
            backgroundColor: '#f5f5f5',
        },
        '&:disabled': {
            cursor: 'not-allowed',
            opacity: 0.5,
        },
    },
    paginationButtonActive: {
        backgroundColor: '#000 !important',
        color: '#fff !important',
        border: '1px solid #000 !important',
    },
    emptyState: {
        textAlign: 'center',
        padding: '60px 20px',
    },
}));

export default function ManagePhotostudioImages(props) {
    const classes = useStyles();
    const {getAccessToken} = useAuth();

    const [eventsList, setEventsList] = useState([]);
    const [selectedEvent, setSelectedEvent] = useState(0);
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filterTab, setFilterTab] = useState('all');
    const [searchText, setSearchText] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [failedImages, setFailedImages] = useState(new Set());
    const [selectedImageDetail, setSelectedImageDetail] = useState(null);
    const [openOrderDialog, setOpenOrderDialog] = useState(false);
    const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({open: false, imageId: null, imageName: null});
    const [deleting, setDeleting] = useState(false);

    const ITEMS_PER_PAGE = 16;

    useEffect(() => {
        if (props.events && props.events.length > 0) {
            const eventList = [
                {id: 0, name: 'Select Event...'},
                ...props.events,
            ];
            setEventsList(eventList);

            if (props.preselectedEventId) {
                setSelectedEvent(props.preselectedEventId);
            }
        }
    }, [props.events, props.preselectedEventId]);

    useEffect(() => {
        if (selectedEvent !== 0) {
            fetchImages();
        }
    }, [selectedEvent]);

    const filteredImages = React.useMemo(() => {
        let filtered = images;

        if (filterTab === 'configured') {
            filtered = filtered.filter(
                img => img.displays && img.displays.length > 0
            );
        } else if (filterTab === 'notConfigured') {
            filtered = filtered.filter(
                img => !img.displays || img.displays.length === 0
            );
        }

        if (searchText.trim()) {
            const search = searchText.toLowerCase();

            filtered = filtered.filter(img =>
                img.name.toLowerCase().includes(search)
            );
        }

        return filtered;
    }, [images, filterTab, searchText]);


    async function getAuthHeader() {
        try {
            const token = await getAccessToken();
            if (!token) throw new Error('Utente non autenticato');
            const cookies = new Cookies();
            const hashCode = cookies.get('hashCode') || '';
            return token + hashCode;
        } catch (err) {
            console.error('Errore getAuthHeader:', err);
            throw err;
        }
    }

    async function fetchImages() {
        setLoading(true);
        try {
            const authHeader = await getAuthHeader();
            const response = await fetch(
                `${process.env.REACT_APP_API_ENDPOINT}photostudio/images?eventID=${selectedEvent}`,
                {
                    method: 'GET',
                    headers: {
                        Authorization: authHeader,
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const data = await response.json();
            const processedImages = (data.items || []).map((item) => ({
                id: item.id,
                name: item.photoStudioImageOriginalUrl,
                orderNumber: item.orderNumber,
                imageUrl: item.photoStudioImageUrl,
                thumbUrl: item.photoStudioThumbImageUrl,
                displays: Array.isArray(item.displays) ? item.displays : [],
                dimensions: item.dimensions,
                fileSize: item.fileSize,
                uploaded: item.uploaded,
            }));
            setImages(processedImages);
            setCurrentPage(1);
        } catch (error) {
            console.error('Errore fetchImages:', error);
            setImages([]);
        } finally {
            setLoading(false);
        }
    }

    function handleChangeEvent(e) {
        setSelectedEvent(e.target.value);
    }

    function handleSearch(e) {
        setSearchText(e.target.value);
    }

    function handleImageClick(imageId) {
        const imageIndex = filteredImages.findIndex(img => img.id === imageId);
        setSelectedImageDetail(imageIndex);
    }

    function handleImageError(imageId) {
        setFailedImages(prev => new Set([...prev, imageId]));
    }

    function handleUploadMore() {
        if (props.onUploadMore) {
            props.onUploadMore(selectedEvent);
        }
    }

    function handleDeleteClick(imageId, imageName) {
        setDeleteConfirmDialog({open: true, imageId, imageName});
    }

    function handleDeleteCancel() {
        setDeleteConfirmDialog({open: false, imageId: null, imageName: null});
    }

    async function handleDeleteConfirm() {
        if (!deleteConfirmDialog.imageId) return;

        setDeleting(true);
        try {
            const authHeader = await getAuthHeader();
            const response = await fetch(
                `${process.env.REACT_APP_API_ENDPOINT}photostudio/${selectedEvent}/${deleteConfirmDialog.imageId}`,
                {
                    method: 'DELETE',
                    headers: {
                        Authorization: authHeader,
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            setDeleteConfirmDialog({open: false, imageId: null, imageName: null});
            fetchImages();
        } catch (error) {
            console.error('Errore eliminazione immagine:', error);
            alert('Errore durante l\'eliminazione dell\'immagine');
        } finally {
            setDeleting(false);
        }
    }

    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const paginatedImages = filteredImages.slice(startIndex, endIndex);
    const totalPages = Math.ceil(filteredImages.length / ITEMS_PER_PAGE);

    const getTabCounts = () => {
        const all = images.length;
        const configured = images.filter(img => img.displays && img.displays.length > 0).length;
        const notConfigured = all - configured;
        return {all, configured, notConfigured};
    };

    const counts = getTabCounts();

    if (selectedImageDetail !== null) {
        return (
            <PhotostudioImageDetail
                imagesList={filteredImages}
                currentImageIndex={selectedImageDetail}
                onBack={() => setSelectedImageDetail(null)}
                selectedEvent={selectedEvent}
                onDataChanged={fetchImages}
            />
        );
    }

    return (
        <div className={classes.root}>
            <Grid item xs={12}>
                <Box className={classes.header}>
                    <Box className={classes.headerContent}>
                        <h3 className="gucci_text" style={{margin: 0, marginBottom: '8px'}}>
                            Event Images
                        </h3>
                        <Typography variant="body2" color="textSecondary">
                            Browse, search and manage your uploaded images. Add hotspots and assign displays.
                        </Typography>
                    </Box>
                </Box>

                {eventsList && eventsList.length > 1 && (
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '20px'
                    }}>
                        <FormControl sx={{minWidth: 400}}>
                            <Select
                                className="gucci_text select_filter"
                                value={selectedEvent}
                                onChange={handleChangeEvent}
                            >
                                {eventsList.map((event) => (
                                    <MenuItem key={`event-${event.id}`} value={event.id}>
                                        {event.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Box sx={{display: 'flex', gap: '12px'}}>
                            {selectedEvent !== 0 && !loading && images.length > 0 && (
                                <Button
                                    variant="contained"
                                    className={classes.button}
                                    startIcon={<Sort sx={{marginRight: '8px'}}/>}
                                    onClick={() => setOpenOrderDialog(true)}
                                >
                                    Manage Images Order
                                </Button>
                            )}
                            <Button
                                variant="contained"
                                className={classes.button}
                                startIcon={<CloudUpload sx={{marginRight: '8px'}}/>}
                                onClick={handleUploadMore}
                            >
                                Upload More Images
                            </Button>
                        </Box>
                    </Box>
                )}

                {selectedEvent !== 0 && (
                    <>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: '20px'
                        }}>

                            <Tabs
                                value={filterTab}
                                onChange={(e, value) => setFilterTab(value)}
                                sx={{
                                    flex: 1,
                                    '& .MuiTab-root': {
                                        color: '#666',
                                        fontFamily: "'GucciSans-Book' !important",
                                        fontSize: '14px',
                                        textTransform: 'none',
                                        minHeight: '40px',
                                        height: '40px',
                                        padding: '0 12px',
                                    },
                                    '& .MuiTab-root.Mui-selected': {
                                        color: 'black',
                                        backgroundColor: '#e0e0e0',
                                        fontFamily: "'GucciSans-Bold' !important",
                                    },
                                    '& .MuiTabs-indicator': {
                                        backgroundColor: 'black',
                                        height: '3px',
                                    },
                                }}
                            >
                                <Tab label={`All (${counts.all})`} value="all"/>
                                <Tab label={`Not Configured (${counts.notConfigured})`} value="notConfigured"/>
                                <Tab label={`Configured (${counts.configured})`} value="configured"/>
                            </Tabs>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                border: '1px solid #e0e0e0',
                                borderRadius: '4px',
                                padding: '8px 12px',
                                width: '300px',
                                height: '40px',
                                backgroundColor: '#e9edef'
                            }}>
                                <Search sx={{marginRight: '8px', color: '#999', fontSize: '18px'}}/>
                                <input
                                    type="text"
                                    placeholder="Search images..."
                                    value={searchText}
                                    onChange={handleSearch}
                                    style={{
                                        flex: 1,
                                        border: 'none',
                                        outline: 'none',
                                        backgroundColor: 'transparent',
                                        fontFamily: "'GucciSans-Book' !important",
                                        fontSize: '14px',
                                    }}
                                />
                            </Box>

                        </Box>

                        {loading ? (
                            <Box sx={{textAlign: 'center', padding: '40px'}}>
                                <CircularProgress/>
                            </Box>
                        ) : paginatedImages.length > 0 ? (
                            <>
                                <Box className={classes.imageGrid}>
                                    {paginatedImages.map((image) => {
                                        const isConfigured = image.displays && image.displays.length > 0;
                                        const hotspotCount = image.displays ? image.displays.length : 0;

                                        return (
                                            <Card
                                                key={image.id}
                                                className={classes.imageCard}
                                                sx={{cursor: 'pointer', position: 'relative'}}
                                                onClick={() => handleImageClick(image.id)}
                                            >
                                                {failedImages.has(image.id) ? (
                                                    <Box
                                                        sx={{
                                                            width: '100%',
                                                            height: '200px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            backgroundColor: '#f5f5f5',
                                                        }}
                                                    >
                                                        <Box sx={{textAlign: 'center'}}>
                                                            <ImageNotSupported sx={{
                                                                fontSize: '48px',
                                                                color: '#ccc',
                                                                marginBottom: '8px'
                                                            }}/>
                                                            <Typography sx={{fontSize: '12px', color: '#999'}}>
                                                                Image not found
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                ) : (
                                                    <Box
                                                        className={classes.imageMedia}
                                                        component="img"
                                                        src={image.thumbUrl || image.imageUrl || '/placeholder.jpg'}
                                                        alt={image.name}
                                                        onError={() => handleImageError(image.id)}
                                                        sx={{
                                                            width: '100%',
                                                            height: '200px',
                                                            objectFit: 'cover',
                                                        }}
                                                    />
                                                )}
                                                <CardContent sx={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}} className={classes.cardContent}>
                                                    <div>
                                                        <Typography className="gucci_text_bold" sx={{
                                                            fontSize: '14px',
                                                            marginBottom: '4px',
                                                            wordBreak: 'break-word'
                                                        }}>
                                                            {image.name}
                                                        </Typography>
                                                        <Box
                                                            className={`${classes.statusBadge} ${
                                                                isConfigured
                                                                    ? classes.configuredBadge
                                                                    : classes.notConfiguredBadge
                                                            }`}
                                                        >
                                                            {isConfigured ? 'Configured' : 'Not configured'}
                                                        </Box>
                                                        {isConfigured && (
                                                            <Typography sx={{fontSize: '12px', color: '#666'}}>
                                                                {hotspotCount} {hotspotCount === 1 ? 'display' : 'displays'}
                                                            </Typography>
                                                        )}
                                                    </div>
                                                    <IconButton
                                                        className={classes.menuButton}
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDeleteClick(image.id, image.name);
                                                        }}
                                                        sx={{
                                                            backgroundColor: 'rgba(255, 255, 255, 0.9) !important',
                                                            '&:hover': {
                                                                backgroundColor: '#fff !important',
                                                            },
                                                        }}
                                                    >
                                                        <Delete sx={{color: '#e74c3c', fontSize: '20px'}}/>
                                                    </IconButton>
                                                </CardContent>
                                            </Card>
                                        );
                                    })}
                                </Box>

                                <Box sx={{textAlign: 'center', marginBottom: '30px'}}>
                                    <Typography variant="body2" color="textSecondary">
                                        Showing {startIndex + 1}-{Math.min(endIndex, filteredImages.length)} of{' '}
                                        {filteredImages.length} images
                                    </Typography>
                                </Box>

                                {totalPages > 1 && (
                                    <Box className={classes.paginationContainer}>
                                        <button
                                            className={classes.paginationButton}
                                            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                            disabled={currentPage === 1}
                                        >
                                            ←
                                        </button>

                                        {Array.from({length: totalPages}, (_, i) => i + 1)
                                            .slice(
                                                Math.max(0, currentPage - 2),
                                                Math.min(totalPages, currentPage + 2)
                                            )
                                            .map((page) => (
                                                <button
                                                    key={`page-${page}`}
                                                    className={`${classes.paginationButton} ${
                                                        page === currentPage ? classes.paginationButtonActive : ''
                                                    }`}
                                                    onClick={() => setCurrentPage(page)}
                                                >
                                                    {page}
                                                </button>
                                            ))}

                                        {totalPages > 4 && currentPage < totalPages - 2 && (
                                            <span style={{padding: '0 8px'}}>...</span>
                                        )}

                                        {totalPages > 4 && currentPage < totalPages - 1 && (
                                            <button
                                                className={classes.paginationButton}
                                                onClick={() => setCurrentPage(totalPages)}
                                            >
                                                {totalPages}
                                            </button>
                                        )}

                                        <button
                                            className={classes.paginationButton}
                                            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                                            disabled={currentPage === totalPages}
                                        >
                                            →
                                        </button>
                                    </Box>
                                )}
                            </>
                        ) : (
                            <Box className={classes.emptyState}>
                                <Typography color="textSecondary">
                                    {searchText ? 'No images found matching your search.' : 'No images available.'}
                                </Typography>
                            </Box>
                        )}
                    </>
                )}

                {selectedEvent === 0 && (
                    <Box className={classes.emptyState}>
                        <Typography color="textSecondary">Please select an event to view images.</Typography>
                    </Box>
                )}
            </Grid>

            <ImageOrderDialog
                open={openOrderDialog}
                onClose={() => setOpenOrderDialog(false)}
                images={images}
                selectedEvent={selectedEvent}
                onOrderUpdated={fetchImages}
                getAuthHeader={getAuthHeader}
            />

            <Dialog
                open={deleteConfirmDialog.open}
                onClose={handleDeleteCancel}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{fontFamily: "'GucciSans-Bold' !important", fontWeight: 'bold'}}>
                    Confirm Delete
                </DialogTitle>
                <DialogContent sx={{paddingTop: '16px'}}>
                    <Typography>
                        Are you sure you want to delete the image?
                    </Typography>
                    {deleteConfirmDialog.imageName && (
                        <Typography sx={{marginTop: '12px', color: '#666', fontSize: '12px'}}>
                            <strong>Image:</strong> {deleteConfirmDialog.imageName}
                        </Typography>
                    )}
                    <Typography sx={{marginTop: '12px', color: '#d32f2f', fontSize: '12px'}}>
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{padding: '16px', gap: '8px'}}>
                    <Button
                        onClick={handleDeleteCancel}
                        disabled={deleting}
                        sx={{
                            color: '#666',
                            textTransform: 'none',
                            fontFamily: "'GucciSans-Bold' !important",
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        disabled={deleting}
                        sx={{
                            backgroundColor: '#e74c3c',
                            color: 'white',
                            textTransform: 'none',
                            fontFamily: "'GucciSans-Bold' !important",
                            '&:hover': {
                                backgroundColor: '#c0392b',
                            },
                            '&:disabled': {
                                backgroundColor: '#999696bf',
                            },
                        }}
                    >
                        {deleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}
